import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import {getDeepCopyOfObject} from 'src/app/shared/shared-utils/object/object.utils';
import {IStore, isStoreOperationallyDisabled} from '../../../shared/shared-models/store/store';
import * as UserStoreActions from './user-store.actions';
import {Features, IFeature, INavigate} from '../models';

export interface IUserStoresState {
  areChangesSaved: boolean;
  isStoreInformationLoading: boolean;
  navigation: INavigate;
  navigationLoadingStartTime: number | null;
  selectedFeature: IFeature;
  selectedMultipleStores: IStore[];
  selectedStore: IStore;
  showLoadingIndicator: boolean;
  stores: IStore[];
}

export const initialUserStoresState: IUserStoresState = {
  areChangesSaved: true,
  isStoreInformationLoading: false,
  navigation: {} as INavigate,
  navigationLoadingStartTime: null,
  selectedFeature: undefined,
  selectedStore: undefined,
  selectedMultipleStores: [],
  showLoadingIndicator: false,
  stores: undefined,
};

const createUserStoresReducer: ActionReducer<IUserStoresState> = createReducer(
  getDeepCopyOfObject(initialUserStoresState),
  //#region GLOBAL
  on(UserStoreActions.setPropertyOnUserStoreState, (state: IUserStoresState, {property, value}) => {
    return {
      ...state,
      [property]: value,
    };
  }),
  on(UserStoreActions.clearUserStoreState, () => ({
    ...getDeepCopyOfObject(initialUserStoresState),
  })),
  //#endregion
  //#region GET STORE INFORMATION
  on(UserStoreActions.getStoresInformation, (state: IUserStoresState) => {
    return {
      ...state,
      isStoreInformationLoading: true,
    };
  }),
  on(UserStoreActions.getStoresInformationSuccess, (state: IUserStoresState, {stores}) => {
    const activeStores: IStore[] = stores.filter((store: IStore) => !isStoreOperationallyDisabled(store));
    let storeFound: IStore | undefined = undefined;
    const stateSelectedStore = state.selectedStore;
    if (stateSelectedStore && !isStoreOperationallyDisabled(stateSelectedStore)) {
      storeFound = activeStores.find((store: IStore) => store.storeId === stateSelectedStore.storeId);
    }
    const storesUpTo30 = activeStores.length <= 30 ? activeStores : activeStores.slice(0, 30);
    return {
      ...state,
      isStoreInformationLoading: false,
      selectedStore: storeFound ? storeFound : activeStores[0],
      selectedMultipleStores: storesUpTo30 ? storesUpTo30 : [],
      stores,
    };
  }),
  //#endregion
  //#region SET SELECTED STORE
  on(UserStoreActions.setAreChangesSaved, (state: IUserStoresState, {areChangesSaved}) => {
    return {
      ...state,
      areChangesSaved,
    };
  }),
  on(UserStoreActions.setSelectedStoreSuccess, (state: IUserStoresState, {selectedStore}) => {
    return {
      ...state,
      selectedStore,
    };
  }),
  on(UserStoreActions.setSelectedStoreById, (state: IUserStoresState, {selectedStoreId}) => {
    const selectedStore = state.stores.find(
      (store: IStore) => store.storeId === selectedStoreId && !isStoreOperationallyDisabled(store),
    );
    const firstActiveStore = state.stores.find((store: IStore) => !isStoreOperationallyDisabled(store));
    return {
      ...state,
      selectedStore: selectedStore ? selectedStore : firstActiveStore ?? state.stores[0],
    };
  }),
  //#endregion
  //#region SET SELECTED STORES
  on(UserStoreActions.setSelectedStores, (state: IUserStoresState, {selected}) => {
    return {
      ...state,
      selectedMultipleStores: selected,
    };
  }),
  on(UserStoreActions.setSelectedStoresById, (state: IUserStoresState, {selected}) => {
    const selectedStores = state.stores.filter((store: IStore) => selected.includes(store.storeId));
    return {
      ...state,
      selectedMultipleStores: selectedStores,
    };
  }),
  //#endregion
  //#region SET SELECTED NAVIGATION
  on(UserStoreActions.setNavigation, (state: IUserStoresState, {navigation, feature}) => {
    const selectedFeature = Features[feature];
    return {
      ...state,
      navigation,
      selectedFeature,
    };
  }),
  on(UserStoreActions.setNavigationSuccess, (state: IUserStoresState, {navigation, feature, savedChanges}) => {
    const selectedFeature = Features[feature];
    const areChangesSaved = savedChanges == undefined;
    return {
      ...state,
      navigation,
      selectedFeature,
      areChangesSaved,
    };
  }),
  on(UserStoreActions.syncNavigationFromRouter, (state: IUserStoresState, {navigation, feature}) => {
    const selectedFeature: IFeature = Features[feature] ?? {
      feature,
      accessCodeBase: [],
      accessCodeBit: [],
    };
    return {
      ...state,
      navigation,
      selectedFeature,
    };
  }),
  on(UserStoreActions.showNavigationLoading, (state: IUserStoresState, {showLoadingIndicator}) => {
    return {
      ...state,
      showLoadingIndicator,
    };
  }),
  //#endregion
  //#region SET SELECTED FEATURE
  on(UserStoreActions.setSelectedFeature, (state: IUserStoresState, {selectedFeature}) => {
    return {
      ...state,
      selectedFeature,
    };
  }),
  //#endregion
);
export const userStoresReducer = (state: IUserStoresState, action: Action): IUserStoresState =>
  createUserStoresReducer(state, action);


